import type { StockItemMovementDto } from '@nest/domain-entities-metadata-generated';
import { CommandButton } from '@nest/internal-frontend';
import {
	Button,
	RelativeDateTime,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useValidationResultDialog
} from '@nest/uikit-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTerminalClient } from '../client-rpc-router';
import { StockItemLinkDetail } from '../components/StockItemLinkDetail';
import { TerminalScreen } from '../components/TerminalScreen';
import { useCommandStockItemPrintLabels } from '../components/useCommandStockItemPrintLabels.hook';
import { useNumberParam } from '../helpers';

export const StockItemDetailPage = () => {
	const client = useTerminalClient();
	const id = useNumberParam('id');
	const [activeTab, setActiveTab] = useState('default');
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);

	const { data, error } = useQuery({
		queryKey: ['stock-item', id],
		queryFn: () => client.wms.getStockItemByIdQuery.execute({ id: id! }),
		enabled: id !== null
	});

	const stockItemLocationsQuery = useQuery({
		queryKey: ['stock-item-locations', id],
		queryFn: () => client.wms.getStockItemLocationsQuery.execute({ id: id! }),
		enabled: id !== null && activeTab === 'locations'
	});

	const MOVEMENTS_PAGE_LIMIT = 30;

	const stockItemMovementsQuery = useInfiniteQuery({
		queryKey: ['stock-item-movements', id],
		queryFn: ({ pageParam }) =>
			client.stockItemMovements.getAll.execute({
				filter: {
					sku: {
						id: id!
					}
				},
				limit: MOVEMENTS_PAGE_LIMIT,
				populate: ['createdBy', 'sourceWarehouseLocation', 'sourcePalette', 'destinationWarehouseLocation', 'destinationPalette'],
				offset: pageParam as number,
				orderBy: {
					id: 'DESC'
				}
			}),
		enabled: id !== null && activeTab === 'movements',
		getNextPageParam: (lastPage, allPages) => (lastPage.length === MOVEMENTS_PAGE_LIMIT ? allPages.length * MOVEMENTS_PAGE_LIMIT : undefined),
		initialPageParam: 0
	});

	useValidationResultDialog([error, stockItemLocationsQuery.error, stockItemMovementsQuery.error]);

	const movements = useMemo(() => stockItemMovementsQuery.data?.pages.flat() as StockItemMovementDto[], [stockItemMovementsQuery.data]);

	const command = useCommandStockItemPrintLabels({ id: id });

	return (
		<TerminalScreen showBackButton topBar={<StockItemLinkDetail stockItemId={id!} />} bottomContent={<div ref={setBottomElementRef} />}>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
				<TabsContent value="default" className="h-full">
					<Table>
						<TableBody>
							<TableRow>
								<TableHead className="w-1/2">Kód</TableHead>
								<TableCell className="w-1/2">{data?.item.code}</TableCell>
							</TableRow>
							<TableRow>
								<TableHead className="w-1/2">Celkem kusů</TableHead>
								<TableCell className="w-1/2">{data?.quantity}</TableCell>
							</TableRow>
							<TableRow>
								<TableHead className="w-1/2">EAN</TableHead>
								<TableCell className="w-1/2">{data?.item.ean}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					<div className="flex justify-end m-4">
						<CommandButton command={command} className="w-full" />
					</div>
				</TabsContent>
				<TabsContent value="locations" className="h-full">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Lokace</TableHead>
								<TableHead>Paleta</TableHead>
								<TableHead>Šarže</TableHead>
								<TableHead className="text-center">Kusů</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{stockItemLocationsQuery.data?.map(location => (
								<TableRow key={location.id}>
									<TableCell>
										<div className="flex flex-col">
											<div className="font-medium">{location.storageLocation.name}</div>
											<div className="text-muted-foreground">{location.storageLocation.code}</div>
										</div>
									</TableCell>
									<TableCell>{location.handlingUnit?.code}</TableCell>
									<TableCell>
										{location.originCountry} {location.batch}
									</TableCell>
									<TableCell className="text-center">{location.quantity}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TabsContent>
				<TabsContent value="movements" className="h-full">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Pohyb</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{movements?.map(movement => (
								<TableRow key={movement.id}>
									<TableCell>
										<div className="flex flex-row gap-2">
											<div>{movement.createdBy?.name}</div>
											<div className="flex-1"></div>
											<RelativeDateTime date={movement.createdAt} />
										</div>
										<div className="text-muted-foreground">
											{movement.sourceWarehouseLocation?.name ?? movement.sourcePalette?.code} →{' '}
											{movement.destinationWarehouseLocation?.name ?? movement.destinationPalette?.code}
										</div>
										<div className="text-sm">{movement.quantity} kusů</div>
									</TableCell>
								</TableRow>
							))}

							{stockItemMovementsQuery.hasNextPage && (
								<TableRow>
									<TableCell>
										<Button variant="secondary" onClick={() => stockItemMovementsQuery.fetchNextPage()} loading={stockItemMovementsQuery.isFetchingNextPage}>
											načíst další
										</Button>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TabsContent>

				{bottomElementRef &&
					createPortal(
						<TabsList className="mt-auto w-full">
							<TabsTrigger value="default" className="w-full">
								<span>Základní</span>
							</TabsTrigger>
							<TabsTrigger value="locations" className="w-full">
								<span>Lokace</span>
							</TabsTrigger>
							<TabsTrigger value="movements" className="w-full">
								<span>Pohyby</span>
							</TabsTrigger>
						</TabsList>,
						bottomElementRef
					)}
			</Tabs>
		</TerminalScreen>
	);
};
